/* Store Location Landing — collection row carousel.

   Progressive enhancement only: the track is a scroll-snap list that already
   works with touch, trackpad and keyboard. This adds page buttons for pointer
   users and hides them whenever the row fits without scrolling. */

class StoreLocationCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    this.nav = this.querySelector('[data-carousel-nav]');
    this.buttons = Array.from(this.querySelectorAll('[data-carousel-direction]'));

    if (!this.track || !this.buttons.length) return;

    this.onScroll = this.syncControls.bind(this);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    this.buttons.forEach((button) => {
      button.addEventListener('click', () => {
        this.page(button.dataset.carouselDirection);
      });
    });

    if ('ResizeObserver' in window) {
      this.observer = new ResizeObserver(this.onScroll);
      this.observer.observe(this.track);
    } else {
      window.addEventListener('resize', this.onScroll);
    }

    this.syncControls();
  }

  disconnectedCallback() {
    if (this.track) this.track.removeEventListener('scroll', this.onScroll);
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('resize', this.onScroll);
  }

  get step() {
    const item = this.track.querySelector('li');
    if (!item) return this.track.clientWidth;

    const styles = window.getComputedStyle(this.track);
    const gap = parseFloat(styles.columnGap) || 0;
    const itemWidth = item.getBoundingClientRect().width + gap;
    const perView = Math.max(1, Math.round(this.track.clientWidth / itemWidth));

    return itemWidth * perView;
  }

  page(direction) {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const distance = direction === 'next' ? this.step : -this.step;

    this.track.scrollBy({
      left: distance,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }

  syncControls() {
    // Round trips through fractional scroll positions leave sub-pixel
    // remainders, so compare with a small tolerance rather than exact equality.
    const tolerance = 2;
    const position = Math.abs(this.track.scrollLeft);
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const overflows = maxScroll > tolerance;

    if (this.nav) this.nav.hidden = !overflows;

    this.buttons.forEach((button) => {
      const isNext = button.dataset.carouselDirection === 'next';
      button.disabled = isNext
        ? position >= maxScroll - tolerance
        : position <= tolerance;
    });
  }
}

if (!customElements.get('store-location-carousel')) {
  customElements.define('store-location-carousel', StoreLocationCarousel);
}
